/**
 * PipelinePage — Kanban-style hiring pipeline.
 *
 * 8 columns mirror the pipeline tag slugs (new → to_review → shortlisted
 * → interview_scheduled → interview_completed → technical_test
 * → offer_sent → rejected). Drag a candidate card from one column to
 * another to update its pipeline tag in one PATCH call.
 *
 * Concurrency: we update local state optimistically before the API
 * call returns, so the move feels instant. On API failure we revert.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
    closestCorners,
} from '@dnd-kit/core';
import { Loader2, GripVertical, Eye, Briefcase, TrendingUp, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { candidatesApi, jobsApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const PIPELINE_SLUGS = [
    'new',
    'to_review',
    'shortlisted',
    'interview_scheduled',
    'interview_completed',
    'technical_test',
    'offer_sent',
    'rejected',
];

// Column accents — distinct hue per stage to make scanning effortless.
const COLUMN_ACCENTS = {
    new:                  { dot: 'bg-blue-500',     header: 'border-t-blue-500',     count: 'bg-blue-100 text-blue-800' },
    to_review:            { dot: 'bg-sky-500',      header: 'border-t-sky-500',      count: 'bg-sky-100 text-sky-800' },
    shortlisted:          { dot: 'bg-indigo-500',   header: 'border-t-indigo-500',   count: 'bg-indigo-100 text-indigo-800' },
    interview_scheduled:  { dot: 'bg-violet-500',   header: 'border-t-violet-500',   count: 'bg-violet-100 text-violet-800' },
    interview_completed:  { dot: 'bg-fuchsia-500',  header: 'border-t-fuchsia-500',  count: 'bg-fuchsia-100 text-fuchsia-800' },
    technical_test:       { dot: 'bg-amber-500',    header: 'border-t-amber-500',    count: 'bg-amber-100 text-amber-800' },
    offer_sent:           { dot: 'bg-emerald-500',  header: 'border-t-emerald-500',  count: 'bg-emerald-100 text-emerald-800' },
    rejected:             { dot: 'bg-rose-500',     header: 'border-t-rose-500',     count: 'bg-rose-100 text-rose-800' },
};


/* -------------------- Card -------------------- */

function CandidateCard({ candidate, onView, isDragging = false, isOverlay = false }) {
    const { attributes, listeners, setNodeRef, transform, isDragging: dragging } = useDraggable({
        id: candidate.id,
        data: { candidate },
        disabled: isOverlay,
    });

    const style = transform && !isOverlay
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;

    const score = candidate.score?.overall;
    const scoreBadge = typeof score === 'number'
        ? (score >= 80 ? 'bg-emerald-100 text-emerald-700' : score >= 60 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')
        : 'bg-slate-100 text-slate-600';

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            className={`group relative bg-white border border-[#E5E7EB] rounded-md shadow-sm hover:shadow-md transition-shadow ${
                (dragging || isDragging) && !isOverlay ? 'opacity-40' : ''
            } ${isOverlay ? 'shadow-xl ring-2 ring-[#002FA7]' : ''}`}
            data-testid={`pipeline-card-${candidate.id}`}
        >
            <div className="flex items-start gap-2 p-3">
                {/* Drag handle */}
                <button
                    type="button"
                    {...listeners}
                    aria-label="drag"
                    className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#4B5563] mt-0.5"
                    data-testid={`pipeline-card-handle-${candidate.id}`}
                >
                    <GripVertical className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                            {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-[#0A0A0A] truncate">
                                {candidate.name || '—'}
                            </p>
                            {candidate.matched_job_title && (
                                <p className="text-[11px] text-[#6B7280] truncate flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" />
                                    {candidate.matched_job_title}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {typeof score === 'number' && (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreBadge}`}>
                                <TrendingUp className="w-3 h-3" />
                                {Math.round(score)}
                            </span>
                        )}
                        {candidate.experience_years > 0 && (
                            <span className="text-[10px] text-[#6B7280]">
                                {candidate.experience_years} {candidate.experience_years > 1 ? 'ans' : 'an'}
                            </span>
                        )}
                    </div>
                    {(candidate.skills || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {(candidate.skills || []).slice(0, 3).map((s, i) => (
                                <span key={i} className="text-[10px] px-1.5 py-0.5 bg-[#EEF2FF] text-[#4338CA] rounded">
                                    {s}
                                </span>
                            ))}
                            {(candidate.skills || []).length > 3 && (
                                <span className="text-[10px] text-[#9CA3AF]">+{candidate.skills.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onView?.(candidate.id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-[#9CA3AF] hover:text-[#002FA7]"
                    aria-label="view"
                    data-testid={`pipeline-card-view-${candidate.id}`}
                >
                    <Eye className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}


/* -------------------- Column -------------------- */

function PipelineColumn({ slug, candidates, onView }) {
    const { t } = useTranslation();
    const accent = COLUMN_ACCENTS[slug] || COLUMN_ACCENTS.new;
    const { setNodeRef, isOver } = useDroppable({ id: slug });

    return (
        <div
            ref={setNodeRef}
            className={`flex flex-col w-[280px] flex-shrink-0 bg-[#F9FAFB] rounded-lg border-t-4 ${accent.header} border-x border-b border-[#E5E7EB] overflow-hidden transition-colors ${
                isOver ? 'ring-2 ring-[#002FA7] bg-[#EEF2FF]' : ''
            }`}
            data-testid={`pipeline-column-${slug}`}
        >
            <div className="px-3 py-2.5 bg-white border-b border-[#E5E7EB] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${accent.dot}`} />
                    <h3 className="text-sm font-semibold text-[#0A0A0A]">{t(`tags.${slug}`)}</h3>
                </div>
                <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold ${accent.count}`} data-testid={`pipeline-count-${slug}`}>
                    {candidates.length}
                </span>
            </div>
            <div className="flex-1 px-2 py-2 space-y-2 min-h-[180px] max-h-[calc(100vh-280px)] overflow-y-auto">
                {candidates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-[#9CA3AF] text-xs italic">
                        <Inbox className="w-6 h-6 mb-1" />
                        {isOver ? t('pipeline.drop_here') : t('pipeline.no_candidates')}
                    </div>
                ) : (
                    candidates.map((c) => (
                        <CandidateCard key={c.id} candidate={c} onView={onView} />
                    ))
                )}
            </div>
        </div>
    );
}


/* -------------------- Page -------------------- */

export default function PipelinePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [columns, setColumns] = useState({}); // slug → array<candidate>
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [jobFilter, setJobFilter] = useState('all');
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor),
    );

    useEffect(() => {
        loadJobs();
    }, []);

    useEffect(() => {
        loadBoard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobFilter]);

    const loadJobs = async () => {
        try {
            const res = await jobsApi.getJobs();
            setJobs(res.data.jobs || []);
        } catch { /* ignore */ }
    };

    const loadBoard = async () => {
        setLoading(true);
        try {
            const jobId = jobFilter !== 'all' ? jobFilter : null;
            const res = await candidatesApi.getPipelineBoard(jobId);
            const map = {};
            for (const col of (res.data.columns || [])) {
                map[col.slug] = col.candidates || [];
            }
            setColumns(map);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setLoading(false);
        }
    };

    const totalCandidates = useMemo(
        () => Object.values(columns).reduce((sum, list) => sum + list.length, 0),
        [columns],
    );

    const findColumnContaining = (id) => {
        for (const slug of PIPELINE_SLUGS) {
            if ((columns[slug] || []).some((c) => c.id === id)) return slug;
        }
        return null;
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over) return;
        const candidateId = active.id;
        const sourceSlug = findColumnContaining(candidateId);
        const targetSlug = over.id;
        if (!sourceSlug || !targetSlug || sourceSlug === targetSlug) return;
        if (!PIPELINE_SLUGS.includes(targetSlug)) return;

        const candidate = columns[sourceSlug].find((c) => c.id === candidateId);
        if (!candidate) return;

        // Optimistic update.
        const previousState = columns;
        setColumns((prev) => {
            const next = { ...prev };
            next[sourceSlug] = prev[sourceSlug].filter((c) => c.id !== candidateId);
            next[targetSlug] = [{ ...candidate }, ...(prev[targetSlug] || [])];
            return next;
        });

        try {
            await candidatesApi.movePipeline(candidateId, targetSlug);
            toast.success(t('pipeline.moved_to', { name: t(`tags.${targetSlug}`) }));
        } catch (error) {
            // Revert on failure.
            setColumns(previousState);
            toast.error(formatApiError(error) || t('pipeline.move_failed'));
        }
    };

    const activeCandidate = useMemo(() => {
        if (!activeId) return null;
        for (const slug of PIPELINE_SLUGS) {
            const found = (columns[slug] || []).find((c) => c.id === activeId);
            if (found) return found;
        }
        return null;
    }, [activeId, columns]);

    return (
        <div className="space-y-4" data-testid="pipeline-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                        {t('pipeline.title')}
                    </h1>
                    <p className="text-[#4B5563] text-sm">{t('pipeline.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={jobFilter} onValueChange={setJobFilter}>
                        <SelectTrigger className="w-[220px]" data-testid="pipeline-job-filter">
                            <SelectValue placeholder={t('pipeline.filter_by_job')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('pipeline.all_jobs')}</SelectItem>
                            {jobs.map((job) => (
                                <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-[#6B7280] hidden sm:inline" data-testid="pipeline-total">
                        {t(`pipeline.candidate_count_${totalCandidates === 1 ? 'one' : 'other'}`, { count: totalCandidates })}
                    </span>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
                </div>
            ) : totalCandidates === 0 ? (
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center" data-testid="pipeline-empty">
                    <Inbox className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" />
                    <h3 className="text-lg font-semibold text-[#0A0A0A]">{t('pipeline.empty_board_title')}</h3>
                    <p className="text-[#4B5563] mt-2">{t('pipeline.empty_board_message')}</p>
                    <Button className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white" onClick={() => navigate('/dashboard/candidates')}>
                        {t('candidates.import_cvs')}
                    </Button>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="overflow-x-auto pb-2">
                        <div className="flex gap-3 min-w-max">
                            {PIPELINE_SLUGS.map((slug) => (
                                <PipelineColumn
                                    key={slug}
                                    slug={slug}
                                    candidates={columns[slug] || []}
                                    onView={(id) => navigate(`/dashboard/candidates/${id}`)}
                                />
                            ))}
                        </div>
                    </div>
                    <DragOverlay>
                        {activeCandidate ? (
                            <CandidateCard candidate={activeCandidate} isOverlay />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}
        </div>
    );
}
